import { MCP_TOOL_GROUPS, type SettingInput } from "@esi/shared";

/**
 * Which control edits a setting in place (openspec change settings-typed-controls, design D1,
 * D7, D8): the catalogue's `input` decides by default, and this table fixes what the type alone
 * does not say (segmented or select, units, what an empty number means, explicit editors).
 * Plain data, no React. Used by the backoffice and the console (projects: slice B).
 */

/** A unit word, `settingsControls.units.<unit>`. */
export type SettingUnit = "hours" | "days" | "mb" | "pages" | "usd" | "percent";

/** What the empty value of a nullable number means. */
export type NullableMeaning = "off" | "noLimit" | "operator";

/** A segment's short label (one or two words), as an i18n key. */
export type SegmentLabelKey =
  | "settingsControls.segments.optional"
  | "settingsControls.segments.required"
  | "settingsControls.segments.euOnly"
  | "settingsControls.segments.global"
  | "settingsControls.segments.fast"
  | "settingsControls.segments.balanced"
  | "settingsControls.segments.deep"
  | "settingsControls.segments.composedOnly"
  | "settingsControls.segments.everyPage";

/** The named button that opens an explicit editor, `settingsControls.editors.<editor>`. */
export type EditorName =
  | "levels"
  | "domains"
  | "guidelines"
  | "placement"
  | "applications"
  | "models";

export type ControlSpec =
  | { kind: "switch" }
  | { kind: "segmented"; options: readonly { value: string; label: SegmentLabelKey }[] }
  | { kind: "select" }
  | { kind: "number"; unit: SettingUnit; prefix?: true }
  | {
      kind: "switchNumber";
      unit: SettingUnit;
      empty: NullableMeaning;
      /** The value switching on saves; null: the field opens empty and its first value saves. */
      onValue: number | null;
    }
  | { kind: "chips" }
  | { kind: "text" }
  /** `tools.egress`: one segmented line per readers audience (D10). */
  | { kind: "egress" }
  | { kind: "editor"; editor: EditorName };

const EFFORTS = [
  { value: "fast", label: "settingsControls.segments.fast" },
  { value: "chat", label: "settingsControls.segments.balanced" },
  { value: "reasoning", label: "settingsControls.segments.deep" },
] as const;

/** Keys whose control is not the default for their input type. */
const CONTROLS: Readonly<Record<string, ControlSpec>> = {
  "auth.mfa": {
    kind: "segmented",
    options: [
      { value: "optional", label: "settingsControls.segments.optional" },
      { value: "required", label: "settingsControls.segments.required" },
    ],
  },
  "models.residency": {
    kind: "segmented",
    options: [
      { value: "eu", label: "settingsControls.segments.euOnly" },
      { value: "global", label: "settingsControls.segments.global" },
    ],
  },
  "models.defaultEffort": { kind: "segmented", options: EFFORTS },
  "tools.webFetch.approval": {
    kind: "segmented",
    options: [
      { value: "provenance", label: "settingsControls.segments.composedOnly" },
      { value: "always", label: "settingsControls.segments.everyPage" },
    ],
  },
  "directory.idpGroupsMaxAge": { kind: "number", unit: "hours" },
  "models.turnMaxUsd": { kind: "number", unit: "usd", prefix: true },
  // The triage assistant's daily spend (feedback-triage-assist D5): an alert, and an optional
  // cap that is off while empty.
  "feedback.triageAlertUsd": { kind: "number", unit: "usd", prefix: true },
  "feedback.triageDailyCapUsd": {
    kind: "switchNumber",
    unit: "usd",
    empty: "noLimit",
    onValue: 10,
  },
  "models.sharePressureThresholdPct": { kind: "number", unit: "percent" },
  "knowledge.maxUploadMb": { kind: "number", unit: "mb" },
  "knowledge.ocrMaxPages": { kind: "number", unit: "pages" },
  "memory.retentionDays": { kind: "number", unit: "days" },
  "knowledge.ocrMonthlyPages": {
    kind: "switchNumber",
    unit: "pages",
    empty: "operator",
    // The operator's number is not in the catalogue: the field opens empty (coordinator, 2026-10-31).
    onValue: null,
  },
  "wiki.reviewAfterDays": { kind: "switchNumber", unit: "days", empty: "off", onValue: 180 },
  "tools.webSearch.maxAgeDays": {
    kind: "switchNumber",
    unit: "days",
    empty: "noLimit",
    onValue: 365,
  },
  "branding.displayName": { kind: "text" },
  "knowledge.classificationScheme": { kind: "editor", editor: "levels" },
  "tools.webSearch.domains": { kind: "editor", editor: "domains" },
  "tools.webFetch.domains": { kind: "editor", editor: "domains" },
  "wiki.guidelines": { kind: "editor", editor: "guidelines" },
  "wiki.placement": { kind: "editor", editor: "placement" },
  "auth.nativeClients": { kind: "editor", editor: "applications" },
  "models.allowedModels": { kind: "editor", editor: "models" },
  "tools.egress": { kind: "egress" },
  // The isolated sandbox's own floors policy (agent-turn D17): the same per-audience lines.
  "tools.codeExecution.egress": { kind: "egress" },
};

/** Every key with an explicit entry (the rest follow their input type). */
export const CONTROL_KEYS: readonly string[] = Object.keys(CONTROLS);

/** The control for a key: its own entry, else the default for its input type. */
export function controlOf(key: string, input: SettingInput): ControlSpec {
  const own = Object.hasOwn(CONTROLS, key) ? CONTROLS[key] : undefined;
  if (own) {
    return own;
  }
  switch (input.type) {
    case "boolean":
      return { kind: "switch" };
    case "select":
      return { kind: "select" };
    case "multiselect":
      return { kind: "chips" };
    case "number":
      // A number with no unit of its own: a plain field (every shown key has one; a test checks).
      return { kind: "number", unit: "days" };
    case "domains":
      return { kind: "editor", editor: "domains" };
    case "json":
      // A structured value with no control of its own reads in words (every shown key has one).
      return { kind: "text" };
  }
}

/** Whether a key's input type alone gives it a fitting control (no entry needed). */
export function hasDefaultControl(input: SettingInput): boolean {
  return input.type === "boolean" || input.type === "select" || input.type === "multiselect";
}

/**
 * The loosening sentence of a key, `settingsControls.loosen.<copy>`: the copy name of the key
 * (`SETTING_COPY`), one per key `settingLoosens` lists.
 */
export const LOOSEN_COPY: Readonly<Record<string, string>> = {
  "tools.webSearch": "webSearch",
  "tools.webFetch": "webFetch",
  "mcp.enabled": "mcpEnabled",
  "mcp.writeTools": "mcpWriteTools",
  "wiki.mcpSelfPublish": "mcpSelfPublish",
  ...Object.fromEntries(MCP_TOOL_GROUPS.map((g) => [`mcp.tools.${g}`, "mcpTools"])),
  "knowledge.declassification": "declassification",
  "auth.deviceGrant": "deviceGrant",
  "auth.nativeClients": "nativeClients",
  "projects.shareOutsideEntity": "shareOutsideEntity",
  "auth.mfa": "mfa",
  "models.residency": "residency",
  "wiki.trust": "wikiTrust",
  "tools.webFetch.approval": "webFetchApproval",
  "mcp.classificationCeiling": "mcpCeiling",
  "tools.webSearch.maxAgeDays": "webMaxAge",
  "tools.webSearch.domains": "webDomains",
  "tools.webFetch.domains": "webFetchDomains",
  // A fallback: the egress row words each audience's loosening itself (D10).
  "tools.egress": "egress",
  "tools.codeExecution": "codeExecution",
  "tools.codeExecution.egress": "codeEgress",
  // Edited in the console's Providers section, which words it per provider (model-picking D1).
  "models.providers": "providers",
  // An unblock (console Labs) or a tenant's opt-in (model-picking D1, D17, slice 4c).
  "models.labs": "labs",
};
